<div class="page-container">
  <div class="max-content-width spaced-sections">
    <!-- Header -->
    <div class="flex justify-between items-start">
      <div>
        <h1 class="page-title">Groups</h1>
        <p class="page-subtitle">View and manage all research groups you belong to.</p>
      </div>
      <button type="button" (click)="openCreateModal()" class="btn-primary mt-3">
        + Create Group
      </button>
    </div>

    <!-- Search Section -->
    <section class="card card-padding">
      <div class="section-spacing">
        <div class="input-group">
          <label class="input-label">Search Groups</label>
          <input
            type="text"
            [value]="searchQuery()"
            (input)="onSearchChange($any($event.target).value)"
            placeholder="Search by group name or description..."
            class="input-field"
          />
        </div>

        @if (hasActiveFilters()) {
          <div class="mt-3">
            <button type="button" (click)="clearFilters()" class="btn-secondary">
              Clear Filters
            </button>
          </div>
        }
      </div>
    </section>

    <!-- Results Count -->
    <div class="flex-between">
      <p class="results-counter">
        Showing
        <span class="results-counter-count">{{ totalItems() }}</span>
        @if (totalItems() === 1) {
          group
        } @else {
          groups
        }
      </p>
    </div>

    <!-- Groups List -->
    @if (loading()) {
      <div class="section-spacing">
        <div class="loading-skeleton"></div>
        <div class="loading-skeleton"></div>
        <div class="loading-skeleton"></div>
      </div>
    } @else if (totalItems() === 0) {
      <section class="card card-padding">
        <div class="empty-state">
          <div class="empty-state-icon">📚</div>
          <h3 class="empty-state-title">No groups found</h3>
          <p class="empty-state-message">
            @if (searchQuery().length > 0) {
              No groups match your search. Try a different search term.
            } @else {
              You are not part of any groups yet. Create your first group to start collaborating.
            }
          </p>
          @if (searchQuery().length === 0) {
            <button type="button" (click)="openCreateModal()" class="mt-6 btn-primary">
              Create your first group
            </button>
          }
        </div>
      </section>
    } @else {
      <section class="card">
        <div class="divider-y">
          @for (group of groups(); track group.id) {
            <div class="list-item">
              <div class="flex items-start gap-4">
                <div class="avatar-medium">
                  {{ group.name.charAt(0).toUpperCase() }}
                </div>

                <div class="min-w-0 flex-1">
                  <div class="flex items-center gap-2">
                    <button
                      (click)="viewGroupDetails(group)"
                      class="text-base font-semibold text-gray-900 -mt-0.5 hover:underline hover:text-secondary transition-colors"
                    >
                      {{ group.name }}
                    </button>
                    <span class="badge-primary">
                      {{ isGroupOwner(group) ? 'Owner' : 'Member' }}
                    </span>
                  </div>
                  @if (group.description) {
                    <p class="line-clamp-2 mt-0.5 max-w-2xl text-sm text-gray-600">
                      {{ group.description }}
                    </p>
                  } @else {
                    <p class="mt-0.5 text-sm text-gray-400">No description provided.</p>
                  }
                </div>
              </div>

              <div class="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  (click)="copyInviteLink(group)"
                  [disabled]="!group.inviteActive || !group.inviteToken"
                  class="btn-small-secondary"
                >
                  {{ copiedInviteGroupId === group.id ? 'Copied' : 'Copy invite link' }}
                </button>

                @if (isGroupOwner(group)) {
                  <button type="button" (click)="openEditModal(group)" class="btn-small-text w-16">
                    Edit
                  </button>
                } @else {
                  <button
                    type="button"
                    (click)="askLeaveGroup(group)"
                    class="btn-small-danger w-16"
                  >
                    Leave
                  </button>
                }
                <button type="button" (click)="viewGroupDetails(group)" class="btn-small-primary">
                  Details
                </button>
              </div>
            </div>
          }
        </div>

        @if (totalPages() > 1) {
          <div
            class="flex items-center justify-between px-6 py-3 text-sm text-gray-600 border-t border-slate-200"
          >
            <button
              type="button"
              (click)="goToPage(currentPage() - 1)"
              [disabled]="!canPrev()"
              class="btn-small-secondary disabled:cursor-not-allowed disabled:opacity-40"
            >
              « Prev
            </button>
            <span>Page {{ currentPage() + 1 }} of {{ totalPages() }}</span>
            <button
              type="button"
              (click)="goToPage(currentPage() + 1)"
              [disabled]="!canNext()"
              class="btn-small-secondary disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next »
            </button>
          </div>
        }
      </section>
    }
  </div>
</div>

<app-group-modal
  [open]="showCreateModal"
  (openChange)="showCreateModal = $event"
  (created)="onGroupCreated()"
></app-group-modal>

@if (selectedGroup) {
  <app-group-edit-modal
    [open]="showEditModal"
    [group]="selectedGroup"
    (openChange)="showEditModal = $event"
    (saved)="saveGroup($event)"
    (deleted)="deleteGroup($event)"
  ></app-group-edit-modal>
}

<app-confirm-modal
  [open]="showLeaveConfirm"
  [title]="'Leave ' + groupToLeave?.name"
  [message]="leaveConfirmMessage"
  [confirmText]="'Leave Group'"
  [cancelText]="'Cancel'"
  [confirmVariant]="'danger'"
  size="sm"
  (openChange)="showLeaveConfirm = $event"
  (confirmed)="confirmLeaveGroup()"
  (canceled)="cancelLeaveGroup()"
></app-confirm-modal>
